import {css, html, LitElement} from 'lit';
import {customElement, property} from 'lit/decorators';
import {
  containerStyles,
  moduleSpacingStyles,
  normalizeStyles,
  typographyStyles
} from '@webs/components-library/theming';
import {Career} from './career';
import {ifDefined} from 'lit-html/directives/if-defined.js';
import {MultiSelectionFilter} from '@webs/components-library/filters-dropdown';

@customElement('webs-cards-careers')
export class CardsCareers extends LitElement {
  private offset = 0;
  private careers: Career[] = [];
  private totalCount = 0;
  private filters: Map<string, string[]> = new Map();

  static styles = [
    normalizeStyles,
    containerStyles,
    typographyStyles,
    moduleSpacingStyles,
    css`
      ::slotted([slot="title"]) {
        text-align: center !important;
      }

      webs-filters-dropdown {
        display: block;
      }
      .filters {
        padding-bottom: .8rem;
      }

      .filters p {
        margin: 0 0 1.6rem;
        text-align: center;
      }

      .filters p span {
        font-weight: var(--font-weight-strong);
        text-decoration: underline;
      }

      webs-grid-row {
        margin-bottom: -1.2rem;
      }

      webs-card-career {
        display: block;
        margin-bottom: 1.2rem;
      }

      .m-cards-careers__bottom {
        margin-top: 1.6rem;
        text-align:center;
      }

      .hidden {
        display: none;
      }


      @media only screen and (min-width: 768px){
        .filters {
          align-items: center;
          display: flex;
        }

        .filters p {
          order: 1;
          margin-bottom: 0;
          margin-left: .8rem;

          text-align: left;
        }
      }

      @media only screen and (min-width: 992px){
        ::slotted([slot="title"]) {
          margin-bottom: 0 !important;
        }

        .filters {
          padding: 4rem 0 1.6rem;
        }

        .m-cards-careers__bottom {
          margin-top: 3.2rem;
        }

        .m-cards-careers__bottom p {
          color: var(--color-grey);
          margin: 0 0 .8rem;
        }
      }
    `
  ];

  @property()
  url: string;

  @property({attribute: 'button-text'})
  buttonText = 'Load more';

  @property({type: Number})
  limit: number;

  @property({attribute: 'space-top'})
  spaceTop = '0';

  @property({attribute: 'space-bottom'})
  spaceBottom = 'm';

  @property()
  id: string;

  connectedCallback() {
    super.connectedCallback();

    let ticking = false;

    document.addEventListener('scroll', (e) => {
      if (!ticking && window.innerWidth < 576) {
        window.requestAnimationFrame(() => {
          const items = this.shadowRoot.querySelectorAll('webs-card-career');
          const cardHeight = 200;
          items.forEach(element => {
            const card = element.shadowRoot.querySelector('.m-card-career');
            if (element.getBoundingClientRect().top >= ((window.innerHeight / 2) - cardHeight)) {
              card.classList.add('active');
            } else {
              card.classList.remove('active');
            }
            if (element.getBoundingClientRect().top >= (window.innerHeight / 2)) {
              card.classList.remove('active');
            }
          });
          ticking = false;
        });

        ticking = true;
      }
    });
  }

  protected render(): unknown {
    return html`
      <section id="${ifDefined(this.id)}"
               class="m-cards-careers ${this.spaceTop ? 'module_top-padding-' + this.spaceTop : ''} ${this.spaceBottom ? 'module_bottom-padding-' + this.spaceBottom : ''}">
        <div class="container">

          <slot name="title"></slot>
          <div class="filters">
            <p>${this.constructFiltersInfo()}</p>

            <webs-filters-dropdown url="${this.url}" limit="${this.limit}" start-at="${this.offset}"
                                   @resourceschange="${this.refreshListing}" @filteradd="${this.onFilterAdd}"
                                   @resetfilterall="${this.onFilterAdd}" @searchcomplete="${this.onFilterAdd}">
              <slot></slot>
            </webs-filters-dropdown>
          </div>
          ${this.careers && this.careers.length > 0 ? html`
            <webs-grid-row>
              ${this.careers.map((item, index) => {
                return html`
                  <webs-card-career md="6"
                                    href="${ifDefined(item.link)}"
                                    career="${ifDefined(item.position)}"
                                    city="${ifDefined(item.city)}"
                                    type="${ifDefined(item.type)}"
                                    tag="${ifDefined(item.category)}">
                  </webs-card-career>
                `
              })}
            </webs-grid-row>

            <div class="m-cards-careers__bottom">
              <p>Showing ${this.careers.length} of out ${this.totalCount}</p>
              <webs-button text="${this.buttonText}" class="${this.totalCount > this.careers.length ? '' : 'hidden'}"
                           @click="${this.onPageChange}"></webs-button>
            </div>
          ` : ''}
        </div>
      </section>
    `
  }

  private onPageChange(): void {
    this.offset = this.offset + this.limit;
    this.requestUpdate();
  }

  private onFilterAdd(e): void {
    this.offset = 0;
    this.careers = [];
    this.totalCount = 0;

    const filter: MultiSelectionFilter = e.detail as MultiSelectionFilter;
    this.filters.set(filter.key, filter.activeFilters);
  }

  private refreshListing(event: CustomEvent): void {
    const data = event.detail.objects;
    this.totalCount = event.detail.totalCount;
    for (let i = 0; i < data.length; i++) {
      let jobType = (typeof data[i].values['filter_job_type'] === 'object') ? data[i].values['filter_job_type'].name : data[i].values['filter_job_type'];
      const category = (typeof data[i].values['category'] === 'object') ? data[i].values['category'].name : data[i].values['category'];
      const city = (typeof data[i].values['filter_location'] === 'object') ? data[i].values['filter_location'].name : data[i].values['filter_location'];

      if (data[i].values['job_type_extra_info']) {
        jobType = `${jobType} (${data[i].values['job_type_extra_info']})`;
      }
      this.careers.push({
        position: data[i].values['position'],
        city: city,
        type: jobType,
        category: category,
        link: data[i].values['link']
      });
    }

    this.requestUpdate();
  }

  private constructFiltersInfo(): unknown {
    let filtersInfo: unknown;

    if (this.careers) {
      filtersInfo = this.careers.length === 1 ? html`<span>1</span> job` : html`<span>${this.totalCount}</span> jobs`;
    }

    //todo: replace hardcoded keys/default values
    const locations = this.getListValue('filter_location', 'all locations');
    const jobTypes = this.getListValue('filter_job_type', 'all job types');

    filtersInfo = html`${filtersInfo} in <span>${locations}</span> in <span>${jobTypes}</span>`;

    return filtersInfo;
  }

  private getListValue(key: string, defaultValue: string): string {
    return this.filters.has(key) && this.filters.get(key).length > 0 ? this.filters.get(key).join(',') : defaultValue;
  }
}
